import { Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { WritingAssistMenuItem, WRITING_ASSIST_MENU } from '../../services/izzy-writing-assist.service';

/** Where the assistant panel is in its flow. Owned by the editor; this component only renders it. */
export type WritingAssistPanelState = 'closed' | 'menu' | 'instruction' | 'running' | 'error';

/**
 * The AI writing assistant popover under the editor toolbar: the action menu, the instruction
 * box (Help me write / Custom), the running state and errors. The RESULT is not shown here:
 * the editor writes it straight into the message and shows an inline Keep / Undo / Try again
 * bar under it. Purely presentational: the message editor owns the selection, the request and
 * the apply step.
 */
@Component({
  standalone: false,
  selector: 'izzy-writing-assist-menu',
  templateUrl: './writing-assist-menu.component.html',
  styleUrls: ['./writing-assist-menu.component.css']
})
export class WritingAssistMenuComponent implements OnChanges {
  @Input() state: WritingAssistPanelState = 'closed';
  @Input() items: ReadonlyArray<WritingAssistMenuItem> = WRITING_ASSIST_MENU;
  @Input() selectedItem: WritingAssistMenuItem | null = null;
  @Input() instruction = '';
  /** "Selected text (12 words)" or "Whole message (120 words)". */
  @Input() scopeLabel = '';
  /** Whether there is any text to work on (operations other than Help me write need some). */
  @Input() hasText = true;
  @Input() error = '';
  /** True once a request has been running for a while; shows a reassurance line. */
  @Input() slow = false;

  @Output() pick = new EventEmitter<WritingAssistMenuItem>();
  @Output() instructionChange = new EventEmitter<string>();
  @Output() run = new EventEmitter<void>();
  @Output() back = new EventEmitter<void>();
  @Output() retry = new EventEmitter<void>();
  @Output() discard = new EventEmitter<void>();

  @ViewChild('instructionBox') instructionBox?: ElementRef<HTMLTextAreaElement>;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['state'] && this.state === 'instruction') {
      // The textarea renders on this change; focus it once it exists.
      setTimeout(() => this.instructionBox?.nativeElement.focus(), 0);
    }
  }

  protected onInstructionInput(value: string): void {
    this.instruction = value;
    this.instructionChange.emit(value);
  }

  protected onInstructionKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (this.instruction.trim()) {
        this.run.emit();
      }
    }
  }

  protected itemDisabled(item: WritingAssistMenuItem): boolean {
    return !this.hasText && !item.allowsEmptyText;
  }
}
